import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { TenantProvider } from '@/lib/tenant'
import { useBackendHealth } from '@/lib/useBackendHealth'

const API_BASE = import.meta.env.VITE_API_URL || ''

function TenantStartingUp({ initialLoad }: { initialLoad: boolean }) {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    if (initialLoad) return
    const id = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [initialLoad])

  if (initialLoad) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Skeleton className="h-8 w-48" />
      </div>
    )
  }
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3">
      <p className="text-lg font-medium">サーバーを起動中です...</p>
      <p className="text-sm text-muted-foreground">
        {elapsed < 60
          ? `${elapsed} 秒経過 — 自動的に再接続します`
          : `${Math.floor(elapsed / 60)} 分 ${elapsed % 60} 秒経過 — もうしばらくお待ちください`}
      </p>
    </div>
  )
}

export default function TenantLayout() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const queryClient = useQueryClient()

  const {
    data: tenant,
    isLoading,
    error,
    failureCount,
  } = useQuery({
    queryKey: ['tenant', tenantSlug],
    queryFn: async () => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      let res: Response
      try {
        res = await fetch(`${API_BASE}/api/auth/tenant-config/${tenantSlug}`, {
          signal: controller.signal,
        })
      } catch {
        // タイムアウトまたはネットワークエラー → バックエンド未起動
        const err = new Error('Backend unavailable') as Error & { isServerUnavailable: boolean }
        err.isServerUnavailable = true
        throw err
      } finally {
        clearTimeout(timeoutId)
      }
      if (res.status === 404) {
        const err = new Error('Tenant not found') as Error & { notFound: boolean }
        err.notFound = true
        throw err
      }
      if (!res.ok) {
        const err = new Error('Backend unavailable') as Error & { isServerUnavailable: boolean }
        err.isServerUnavailable = true
        throw err
      }
      const body = await res.json()
      return body.data as { id: string; slug: string; name: string }
    },
    enabled: !!tenantSlug,
    staleTime: Infinity,
    retry: (count, err) => {
      // 404 はリトライしない
      if ((err as Error & { notFound?: boolean }).notFound) return false
      // それ以外（バックエンド未起動）は query-client デフォルトに従う
      return count < 10
    },
    retryDelay: 2000,
  })

  const isNotFound = error && (error as Error & { notFound?: boolean }).notFound
  const isBackendDown = isLoading || (!!error && !isNotFound)

  const handleRecovered = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['tenant', tenantSlug] })
  }, [queryClient, tenantSlug])

  useBackendHealth(isBackendDown && !isNotFound, handleRecovered)

  if (isBackendDown && !isNotFound) {
    return <TenantStartingUp initialLoad={isLoading && failureCount === 0} />
  }

  if (isNotFound || !tenant) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold">テナントが見つかりません</h1>
        <p className="text-muted-foreground">
          「{tenantSlug}」は存在しないか、アクセス権がありません。
        </p>
      </div>
    )
  }

  return (
    <TenantProvider tenant={tenant}>
      <Outlet />
    </TenantProvider>
  )
}
